import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState } from "react";
import logo from "../logo/Legalcastlegroupnbg.png";

interface SplashScreenProps {
  onAccept: () => void;
}

const SplashScreen = ({ onAccept }: SplashScreenProps) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(160deg, #0D2139 0%, #0A1826 55%, #061018 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      {/* Atmospheric glow */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(70,130,180,0.12) 0%, transparent 70%)",
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            maxHeight: "90vh",
            overflow: "auto",
            position: "relative",
            borderRadius: 0,
            bgcolor: "#F2F7FC",
            border: "1px solid rgba(70,130,180,0.15)",
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                backgroundColor: "#0B1F36",
                borderRadius: 0,
                padding: "14px 20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "fit-content",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Legal Castle Group"
                sx={{
                  width: { xs: "100px", sm: "120px" },
                  height: "auto",
                  filter: "brightness(1.1)",
                }}
              />
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            component="h1"
            sx={{
              color: "#0B1F36",
              fontFamily: '"Playfair Display", serif',
              fontWeight: 400,
              textAlign: "center",
              mb: 0.5,
              fontSize: { xs: "1.4rem", md: "1.8rem" },
              "& em": { fontStyle: "italic", color: "#4682B4" },
            }}
            dangerouslySetInnerHTML={{ __html: 'Terms &amp; <em>Conditions</em>' }}
          />

          {/* Decorative line */}
          <Box
            sx={{
              width: 48,
              height: 2,
              background: "linear-gradient(90deg, #4682B4, #2C5F8A)",
              mx: "auto",
              mb: 3,
            }}
          />

          <Typography
            variant="body1"
            paragraph
            sx={{
              mb: 2.5,
              fontSize: "0.95rem",
              color: "#3D4A57",
              textAlign: "center",
            }}
          >
            Welcome to Legal Castle Group. Before proceeding, please read and
            agree to our terms and conditions:
          </Typography>

          {/* Terms list */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
            {[
              {
                num: "01",
                title: "Disclaimer",
                text: "The information provided on this website is for general informational purposes only and does not constitute legal advice.",
              },
              {
                num: "02",
                title: "No Attorney-Client Relationship",
                text: "Use of this website or accessing the information here does not create an attorney-client relationship.",
              },
              {
                num: "03",
                title: "Confidentiality",
                text: "While we strive to protect your information, communication through this website may not be completely secure or confidential.",
              },
              {
                num: "04",
                title: "Accuracy",
                text: "While we make every effort to keep the information on our website current, we make no warranties about the accuracy or completeness of the information provided.",
              },
              {
                num: "05",
                title: "Jurisdiction",
                text: "Legal services are only provided in jurisdictions where we are licensed to practice. The information on this website may not apply to all jurisdictions.",
              },
            ].map((item) => (
              <Box
                key={item.num}
                sx={{
                  display: "flex",
                  gap: 2,
                  p: 2,
                  bgcolor: "#FFFFFF",
                  border: "1px solid rgba(70,130,180,0.12)",
                  transition: "border-color 0.3s ease",
                  "&:hover": { borderColor: "rgba(70,130,180,0.3)" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Cinzel", serif',
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#4682B4",
                    minWidth: 24,
                    pt: 0.2,
                  }}
                >
                  {item.num}
                </Typography>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: "#0B1F36",
                      mb: 0.3,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#3D4A57",
                      lineHeight: 1.7,
                      fontWeight: 400,
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  sx={{
                    color: "#4682B4",
                    "&.Mui-checked": {
                      color: "#4682B4",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.9rem", color: "#3D4A57" }}>
                  I have read and agree to the terms and conditions
                </Typography>
              }
            />
          </Box>

          <Box sx={{ mt: 2.5, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              disabled={!agreed}
              onClick={onAccept}
              sx={{
                fontFamily: '"Cinzel", serif',
                fontSize: "10px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                background: "linear-gradient(135deg, #4682B4 0%, #2C5F8A 100%)",
                color: "#F8FBFF",
                px: 5,
                py: 1.8,
                fontWeight: 600,
                borderRadius: 0,
                boxShadow: "0 6px 24px rgba(70,130,180,0.35)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #5A9AC7 0%, #4682B4 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 32px rgba(70,130,180,0.5)",
                },
                "&:disabled": {
                  backgroundColor: "rgba(70,130,180,0.15)",
                  color: "rgba(11,31,54,0.3)",
                  boxShadow: "none",
                },
              }}
            >
              Accept &amp; Continue
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SplashScreen;
